import React, { useState } from 'react'
import { varClass } from '../shared'
import { TAnomaliesSuppressionScope } from '../shared/types'
import { createSuppression } from './api'

// Modal contents shown via useModal().showModal(...) when the operator
// suppresses an advertiser from a triage or rejection-review row action.
// Picks a scope (what the suppression targets), a duration via preset
// buttons, optionally a reason, then POSTs and tells the parent to refresh.
//
// Duration presets translate to an ISO expires_at on the client. "Indefinite"
// sends null so the suppression only ends on explicit DELETE.
//
// One suppression row = one scope. Suppressing several metrics means opening
// the modal once per metric — the backend has no multi-scope POST or update
// endpoint (change of scope = DELETE + re-create).

type Preset = '7d' | '30d' | '90d' | 'indefinite'

const PRESET_LABEL: Record<Preset, string> = {
  '7d': '7 days',
  '30d': '30 days',
  '90d': '90 days',
  indefinite: 'Indefinite',
}

const expiresAtFor = (preset: Preset): string | null => {
  if (preset === 'indefinite') return null
  const days = preset === '7d' ? 7 : preset === '30d' ? 30 : 90
  const ms = Date.now() + days * 86400000
  return new Date(ms).toISOString()
}

// '' = all daily alerts (maps to primary_metric: null on the wire).
export type SuppressScopeChoice = '' | TAnomaliesSuppressionScope

export const SUPPRESSION_METRIC_LABELS: Record<
  Exclude<TAnomaliesSuppressionScope, 'rejections'>,
  string
> = {
  cr: 'CR',
  epc: 'EPC',
  spc: 'SPC',
  clicks: 'Clicks',
  conversions: 'Conversions',
}

const SCOPE_OPTIONS: { value: SuppressScopeChoice; label: string }[] = [
  { value: '', label: 'All performance alerts' },
  { value: 'cr', label: 'CR alerts only' },
  { value: 'epc', label: 'EPC alerts only' },
  { value: 'spc', label: 'SPC alerts only' },
  { value: 'clicks', label: 'Clicks alerts only' },
  { value: 'conversions', label: 'Conversions alerts only' },
  { value: 'rejections', label: 'Rejection review' },
]

const scopeDescription = (
  scope: SuppressScopeChoice,
  advertiserDomain: string
): string => {
  if (scope === 'rejections') {
    return `Hides ${advertiserDomain} from the Monthly Rejection Review (queue, monthly rows and trend totals) until the suppression expires or is removed. Daily alerts are unaffected.`
  }
  if (scope !== '') {
    return `Hides only ${SUPPRESSION_METRIC_LABELS[scope]} daily alerts for this advertiser from the triage list and summary cards. Alerts on other metrics stay visible, and the rejection review is unaffected.`
  }
  return 'Hides all daily alerts for this advertiser from the triage list and summary cards until the suppression expires or is removed. Does not hide the advertiser from the rejection review.'
}

export const SuppressModal = (props: {
  advertiserDomain: string
  // Pre-selects the scope dropdown (e.g. the row's metric from triage, or
  // 'rejections' from the rejection review). The operator can still change it.
  initialScope?: SuppressScopeChoice
  hideModal: () => void
  onSuppressed: (
    advertiserDomain: string,
    scope: TAnomaliesSuppressionScope | null
  ) => void
}) => {
  const { advertiserDomain, hideModal, onSuppressed } = props
  const [scope, setScope] = useState<SuppressScopeChoice>(
    props.initialScope ?? ''
  )
  const [preset, setPreset] = useState<Preset>('30d')
  const [reason, setReason] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async () => {
    setSubmitting(true)
    setError(null)
    try {
      await createSuppression({
        advertiserDomain,
        expiresAt: expiresAtFor(preset),
        reason: reason.trim() || null,
        primaryMetric: scope || null,
      })
      onSuppressed(advertiserDomain, scope || null)
      hideModal()
    } catch (e: any) {
      setError(e?.message || 'Failed to create suppression. Try again.')
      setSubmitting(false)
    }
  }

  return (
    <div className="card-body anomalies-suppress-modal">
      <div className="text-dark text-bigger text-bolder m-b-3">
        Suppress {advertiserDomain}
      </div>

      <div className="m-b-3">
        <div className="text-dark text-small text-bolder m-b-1">Scope</div>
        <div className="control control-select" style={{ display: 'block' }}>
          <select
            value={scope}
            onChange={(e) => setScope(e.target.value as SuppressScopeChoice)}
            style={{ width: '100%' }}
          >
            {SCOPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <p className="text-light text-small m-t-1" style={{ maxWidth: 520 }}>
          {scopeDescription(scope, advertiserDomain)}
        </p>
      </div>

      <div className="m-b-3">
        <div className="text-dark text-small text-bolder m-b-1">Duration</div>
        <div className="anomalies-rejection-review-view-toggle">
          {(Object.keys(PRESET_LABEL) as Preset[]).map((p) => (
            <button
              key={p}
              type="button"
              className={varClass({
                'anomalies-rejection-review-view-toggle-btn': true,
                active: preset === p,
              })}
              onClick={() => setPreset(p)}
            >
              {PRESET_LABEL[p]}
            </button>
          ))}
        </div>
      </div>

      <div className="m-b-3">
        <div className="text-dark text-small text-bolder m-b-1">
          Reason <span className="text-light">(optional)</span>
        </div>
        <div className="control" style={{ display: 'block' }}>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. partner is paused for Q4 migration"
            rows={3}
            style={{ display: 'block', boxSizing: 'border-box' }}
          />
        </div>
      </div>

      {error && (
        <div className="text-danger text-small m-b-2">{error}</div>
      )}

      <div className="row row-end" style={{ gap: 8 }}>
        <button
          type="button"
          className="btn btn-default"
          onClick={hideModal}
          disabled={submitting}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={submit}
          disabled={submitting}
        >
          {submitting ? 'Saving…' : scope === 'rejections' ? 'Hide' : 'Suppress'}
        </button>
      </div>
    </div>
  )
}
